// =========================
// AudiencePanel — v1.3.0 BL-018b (Admin Portal — Audience Visibility)
//
// Read-only visibility into the durable, owned waitlist audience captured by
// BL-018b: total size, segment composition, and the most recent signups.
// Reads through lib/admin:
//   listWaitlist() → { total, recent, composition }
//
// This is AUDIENCE VISIBILITY, not a CRM — there are no contact/edit/score
// controls (BL-018b §6 exclusions). It exists so audience growth, recency,
// and composition are observable for interview/beta recruitment and launch
// readiness.
//
// Rendered as a tab inside AdminDashboard; route gating
// (ProtectedRoute > AdminRoute) happens above the shell, and admin-get-waitlist
// independently enforces the admin gate server-side (defense-in-depth).
// =========================

import React, { useEffect, useState, useCallback } from 'react';
import { listWaitlist, sendInvitation } from '../../lib/admin';

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString();
}

// Human label for a stored segment key (composition display only).
function segmentLabel(key) {
  if (key === 'unknown') return 'Unknown';
  return key
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export default function AudiencePanel() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Send Invitation (PLATFORM-006 / ADR-064) — minimal operator surface for the
  // Product-owned Invitation Aggregate. Records a verified invitation-sent fact
  // (server publishes participant.invitation_sent). Observability, NOT
  // authorization — recording an invitation does not admit anyone.
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteState, setInviteState] = useState('idle'); // idle | sending | sent | error
  const [inviteMessage, setInviteMessage] = useState(null);

  const handleSendInvitation = useCallback(
    async (e) => {
      e.preventDefault();
      const email = inviteEmail.trim();
      if (!email) return;
      setInviteState('sending');
      setInviteMessage(null);
      const { error: sendError } = await sendInvitation({ email });
      if (sendError) {
        setInviteState('error');
        setInviteMessage(sendError.message || 'Could not send invitation');
      } else {
        setInviteState('sent');
        setInviteMessage(`Invitation recorded for ${email}`);
        setInviteEmail('');
      }
    },
    [inviteEmail]
  );

  const inviteForm = (
    <form
      onSubmit={handleSendInvitation}
      className="card"
      style={{ display: 'grid', gap: 'var(--space-2)', padding: 'var(--space-4)' }}
    >
      <h2
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: 'var(--text-lg)',
          fontWeight: 'var(--weight-bold)',
          color: 'var(--color-white)',
          margin: 0,
        }}
      >
        Send invitation
      </h2>
      <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-steel)', margin: 0 }}>
        Records a controlled-onboarding invitation as an observable Product fact. This does not
        grant access — the onboarding allowlist remains the enforcement mechanism.
      </p>
      <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
        <input
          type="email"
          aria-label="Invitee email"
          placeholder="invitee@example.com"
          value={inviteEmail}
          onChange={(ev) => setInviteEmail(ev.target.value)}
          style={{
            flex: '1 1 240px',
            padding: 'var(--space-2) var(--space-3)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-navy-border)',
            background: 'var(--color-navy-mid)',
            color: 'var(--color-white)',
            fontSize: 'var(--text-base)',
          }}
        />
        <button
          type="submit"
          disabled={inviteState === 'sending' || !inviteEmail.trim()}
          style={{
            padding: 'var(--space-2) var(--space-4)',
            borderRadius: 'var(--radius-md)',
            border: 'none',
            background: 'var(--color-teal)',
            color: 'var(--color-white)',
            fontWeight: 'var(--weight-bold)',
            cursor: 'pointer',
          }}
        >
          {inviteState === 'sending' ? 'Sending…' : 'Send invitation'}
        </button>
      </div>
      {inviteMessage && (
        <div
          role={inviteState === 'error' ? 'alert' : 'status'}
          style={{
            fontSize: 'var(--text-sm)',
            fontWeight: 'var(--weight-semibold)',
            color: inviteState === 'error' ? 'var(--color-danger-lt)' : 'var(--color-success-lt)',
          }}
        >
          {inviteMessage}
        </div>
      )}
    </form>
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: loadError } = await listWaitlist();
    if (loadError) {
      setError(loadError.message || 'Could not load audience');
      setSummary(null);
    } else {
      setSummary(data || { total: 0, recent: [], composition: {} });
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div
        style={{
          padding: 'var(--space-5)',
          color: 'var(--color-steel-lt)',
          fontSize: 'var(--text-base)',
        }}
      >
        Loading…
      </div>
    );
  }

  if (error) {
    return (
      <div
        role="alert"
        style={{
          padding: 'var(--space-4) var(--space-5)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--color-danger)',
          background: 'var(--color-danger-ghost)',
          color: 'var(--color-danger-lt)',
          fontSize: 'var(--text-base)',
          fontWeight: 'var(--weight-semibold)',
        }}
      >
        {error}
      </div>
    );
  }

  const total = summary?.total ?? 0;
  const recent = summary?.recent ?? [];
  const composition = summary?.composition ?? {};
  const compositionEntries = Object.entries(composition).sort((a, b) => b[1] - a[1]);

  return (
    <div style={{ display: 'grid', gap: 'var(--space-5)' }}>
      {/* Send invitation (PLATFORM-006 / ADR-064) */}
      {inviteForm}

      {/* Total audience size */}
      <div className="card-stat" style={{ textAlign: 'left' }}>
        <span
          style={{
            display: 'block',
            fontSize: 'var(--text-sm)',
            textTransform: 'uppercase',
            letterSpacing: 'var(--tracking-wide)',
            color: 'var(--color-steel)',
            marginBottom: 'var(--space-1)',
          }}
        >
          Total audience
        </span>
        <span
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'var(--text-4xl)',
            fontWeight: 'var(--weight-black)',
            color: 'var(--color-white)',
          }}
        >
          {total}
        </span>
      </div>

      {total === 0 ? (
        <div
          style={{
            padding: 'var(--space-5)',
            color: 'var(--color-muted)',
            fontStyle: 'italic',
          }}
        >
          No signups yet
        </div>
      ) : (
        <>
          {/* Segment composition */}
          <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
            <h2
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'var(--text-lg)',
                fontWeight: 'var(--weight-bold)',
                color: 'var(--color-white)',
                margin: 0,
              }}
            >
              Composition
            </h2>
            {compositionEntries.map(([key, n]) => (
              <div
                key={key}
                className="card"
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: 'var(--space-2) var(--space-4)',
                }}
              >
                <span style={{ color: 'var(--color-steel-lt)' }}>{segmentLabel(key)}</span>
                <span
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontWeight: 'var(--weight-bold)',
                    color: 'var(--color-white)',
                  }}
                >
                  {n}
                </span>
              </div>
            ))}
          </div>

          {/* Recent signups */}
          <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
            <h2
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'var(--text-lg)',
                fontWeight: 'var(--weight-bold)',
                color: 'var(--color-white)',
                margin: 0,
              }}
            >
              Recent signups
            </h2>
            {recent.map((row) => (
              <div
                key={row.id}
                className="card"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 'var(--space-4)',
                  flexWrap: 'wrap',
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
                  <span
                    style={{
                      fontWeight: 'var(--weight-bold)',
                      color: 'var(--color-white)',
                    }}
                  >
                    {row.email}
                  </span>
                  <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-steel)' }}>
                    {row.first_name ? `${row.first_name} · ` : ''}
                    Joined {formatDate(row.created_at)}
                  </span>
                </div>
                <span className="badge">{segmentLabel(row.segment || 'unknown')}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
